import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
	DefaultMatterAdjustmentOccupancyFees,
	MatterAdjustmentOccupancyFees,
} from '@models/matter';
import { Select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { cloneDeep } from 'lodash';
import { ICON } from '@core/util/icon';
import { format, getDate, getYear, parseISO } from 'date-fns';
import {
	calculateOccupancyFees,
	OccupancyFeesCalculated,
} from '../../../../shared/services/adjustments';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { AdjustmentsDispatch } from '@state/matter/adjustments';
import { AdjustmentDefaultDetailsSelectors } from '@state/global/adjustment-details';

export interface OccupancyFeesDialogData {
	adjustmentId?: string;
}

@UntilDestroy()
@Component({
	selector: 'app-occupancy-fees-dialog',
	templateUrl: 'occupancy-fees-dialog.component.html',
	standalone: false,
})
export class OccupancyFeesDialogComponent implements OnInit {
	id: string;
	adjustment: MatterAdjustmentOccupancyFees;
	adjustmentDate: string;
	@Select(SelectedMatter.adjustmentDate) adjustmentDate$: Observable<string>;
	@ViewSelectSnapshot(AdjustmentsSelector.occupancyFeesAdjustment)
	occupancyFeesAdjustment: MatterAdjustmentOccupancyFees;
	@SelectSnapshot(SelectedMatter.matterHST) hst: number;
	@SelectSnapshot(
		AdjustmentDefaultDetailsSelectors.adjustmentDetailsOccupancyFees,
	)
	public adjustmentDetails: boolean;
	detailLabel: string;
	readonly ICON = ICON;
	purchaserLabel: string;
	calculation: OccupancyFeesCalculated;
	purchaserPaidItems = [
		{
			label: 'Yes',
			value: 'yes',
		},
		{
			label: 'No',
			value: 'no',
		},
	];

	purchasersShareLabel: string;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: OccupancyFeesDialogData,
		private dialogRef: MatDialogRef<OccupancyFeesDialogComponent>,
		private adjustmentDispatch: AdjustmentsDispatch,
	) {}

	ngOnInit(): void {
		this.adjustment = cloneDeep({
			...DefaultMatterAdjustmentOccupancyFees,
			details: this.adjustmentDetails,
		});
		this.id = this.data?.adjustmentId;
		this.changed();
		if (this.id) {
			this.adjustment = cloneDeep(this.occupancyFeesAdjustment);
		}

		this.adjustmentDate$ // this order is important, `adjustmentDate` should override `closingDate` if it exists
			.pipe(untilDestroyed(this))
			.subscribe((date) => {
				this.adjustmentDate = date;
				this.purchaserLabel = `Purchaser has paid fees for ${format(parseISO(this.adjustmentDate), 'MMMM')}, ${getYear(
					parseISO(this.adjustmentDate),
				)}`;
				this.purchasersShareLabel = `Purchaser's share for ${getDate(parseISO(this.adjustmentDate)) - 1} days`;
				this.changed();
			});
	}

	cancelModal(): void {
		this.dialogRef.close();
	}

	changed(): void {
		if (!this.adjustmentDate) {
			return;
		}

		this.calculation = calculateOccupancyFees(
			this.adjustment.fees,
			this.adjustment.applyHst,
			this.adjustment.purchaserPaid,
			this.adjustmentDate,
			this.hst,
		);

		this.detailLabel = `by purchaser for ${format(parseISO(this.adjustmentDate), 'MMMM')}, ${getYear(parseISO(this.adjustmentDate))}`;
		if (this.adjustment.purchaserPaid === 'yes') {
			this.detailLabel = `Paid ${this.detailLabel}`;
		} else {
			this.detailLabel = `Not paid ${this.detailLabel}`;
		}
	}

	saveModal(): void {
		this.adjustmentDispatch.Set(this.adjustment);
		this.dialogRef.close();
	}
}
